import {createAction} from '@reduxjs/toolkit'
import {hookToAttributeSelector} from '@wix/panda-js-utils'
import {isDonationLessThanMinimum} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {setActiveElement} from '../../../../commons/actions/focus-handler'
import {a11yTicketId} from '../components/event-details/tickets-picker-new/a11y'
import {DONATION_ERROR} from '../constants/donation-error'
import {getSelectedTicketDonation, getSelectedTicketQuantity, isTicketLimitReached} from '../selectors/selected-tickets'
import {getTicketById, getTickets} from '../selectors/tickets'
import {checkDonationError, isEmptyDonation} from '../services/donations'
import {createAsyncAction} from '../services/redux-toolkit'

export interface SelectTicketRequest {
  ticketId: string
  count: number
  pricingOptionId?: string
}

export interface ChangeTicketDonation {
  ticketId: string
  donation: string
}

export interface ChangeTicketDonationResponse {
  ticketId: string
  donation: string
  quantity: number
}

export interface SetDonationError {
  ticketId: string
  error: DONATION_ERROR
}

export const selectTicket = createAsyncAction<SelectTicketRequest, SelectTicketRequest>(
  'SELECT_TICKET',
  async ({ticketId, count, pricingOptionId}, {getState, dispatch, rejectWithValue}) => {
    const state = getState()
    const currentCount = getSelectedTicketQuantity(state, ticketId, pricingOptionId)
    if (currentCount < count) {
      const ticket = getTicketById(getTickets(state), ticketId)
      const error = checkDonationError(ticket, getSelectedTicketDonation(state, ticketId))
      if (error) {
        await dispatch(setDonationError({ticketId, error}))
        if (count > 0) {
          dispatch(setActiveElement(`#${a11yTicketId(ticket)} ${hookToAttributeSelector(DH.DONATION_INPUT)} input`))
        }
        return rejectWithValue({error: 'Invalid donation'})
      }
    }
    return {ticketId, count, pricingOptionId}
  },
)

export const changeTicketDonation = createAsyncAction<ChangeTicketDonationResponse, ChangeTicketDonation>(
  'CHANGE_TICKET_DONATION',
  async ({ticketId, donation}, {getState}) => {
    const state = getState()
    const definition = getTicketById(getTickets(state), ticketId)
    const selectedQuantity = getSelectedTicketQuantity(state, ticketId)

    let quantity = selectedQuantity
    if (isEmptyDonation(donation) || isDonationLessThanMinimum(definition, Number(donation))) {
      quantity = 0
    } else if (!selectedQuantity && !isTicketLimitReached(state)) {
      quantity = 1
    }

    return {
      ticketId,
      donation,
      quantity,
    }
  },
)

export const setDonationError = createAction<SetDonationError>('SET_DONATION_ERROR')
